import { auth, db, fieldValue } from "./firebase.utils";

export const PRIVATE_USER_COLLECTION = "userPrivate";
const PROFILE_COMPLETE_CACHE_PREFIX = "antlogy.profileComplete.v1:";

const profileCompleteCacheKey = (uid) =>
  `${PROFILE_COMPLETE_CACHE_PREFIX}${uid}`;

export const hasCachedPrivateBirthday = (uid) => {
  if (!uid) return false;

  try {
    return window.localStorage.getItem(profileCompleteCacheKey(uid)) === "1";
  } catch {
    return false;
  }
};

export const markPrivateBirthdayComplete = (uid) => {
  if (!uid) return;

  try {
    window.localStorage.setItem(profileCompleteCacheKey(uid), "1");
  } catch {
    // Storage can be unavailable in private browsing or hardened browsers.
    // The next launch will simply perform the Firestore check again.
  }
};

export const DEFAULT_NOTIFICATION_SETTINGS = {
  commentAssignedMob: true,
  commentAssignedWeb: true,
  commentCreatedMob: true,
  commentCreatedWeb: true,
  meetingCancelMob: true,
  meetingCancelWeb: true,
  meetingCancelEmail: true,
  meetingMovedMob: true,
  meetingMovedWeb: true,
  meetingMovedEmail: true,
  meetingNewMob: true,
  meetingNewWeb: true,
  meetingNewEmail: true,
  meetingStartMob: true,
  meetingStartWeb: true,
  meetingStartEmail: true,
  noteAssignedMob: true,
  noteAssignedWeb: true,
  privateMsgMob: true,
  privateMsgWeb: true,
  projectMsgMob: true,
  projectMsgWeb: true,
  taskAssignedMob: true,
  taskAssignedWeb: true,
  taskEndMob: true,
  taskEndWeb: true,
  taskStartMob: true,
  taskStartWeb: true,
  workspaceMsgMob: true,
  workspaceMsgWeb: true,
};

export const makeDefaultDisplayName = (email = "") => {
  const cleaned = (email.split("@")[0] || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, 4);

  const hint = cleaned || "usr";
  const code = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `ant${hint}${code}`;
};

export const toDateOnlyString = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const fromDateOnlyString = (value) => {
  if (typeof value !== "string") return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );

  if (
    date.getFullYear() !== Number(match[1]) ||
    date.getMonth() !== Number(match[2]) - 1 ||
    date.getDate() !== Number(match[3])
  ) {
    return null;
  }

  return date;
};

export const getOldestAllowedBirthDate = () => {
  const today = new Date();
  const cutoff = new Date(
    today.getFullYear() - 16,
    today.getMonth(),
    today.getDate(),
  );

  // Feb 29 normalized into March when the cutoff year is not a leap year.
  // Use the last valid day of February so March 1 births are not admitted early.
  if (cutoff.getMonth() !== today.getMonth()) {
    return new Date(today.getFullYear() - 16, today.getMonth() + 1, 0);
  }

  return cutoff;
};

export const isAtLeast16 = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  return date <= getOldestAllowedBirthDate();
};

const legacyValueToDateOnly = (value) => {
  if (!value) return "";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const date = value?.toDate
    ? value.toDate()
    : value instanceof Date
      ? value
      : new Date(value);

  return toDateOnlyString(date);
};

export const getVerificationActionSettings = () => ({
  url: `${window.location.origin}/verify-email`,
  handleCodeInApp: false,
});

export const getPrivateUserProfile = async (uid) => {
  if (!uid) return null;

  const snapshot = await db.collection(PRIVATE_USER_COLLECTION).doc(uid).get();
  return snapshot.exists ? snapshot.data() : null;
};

export const createOrLoadUserProfile = async (
  firebaseUser,
  userName,
  imgAvatar = null,
  dateOfBirth = null,
) => {
  if (!firebaseUser?.uid) throw new Error("auth/user-required");

  const { uid, email, photoURL } = firebaseUser;
  const userRef = db.collection("users").doc(uid);
  const snapshot = await userRef.get();

  if (!snapshot.exists) {
    const publicProfile = {
      uid,
      email: email || null,
      userName:
        (userName || "").trim() ||
        firebaseUser.displayName ||
        makeDefaultDisplayName(email),
      imgAvatar: imgAvatar || null,
      imgUrl: photoURL || null,
      imageUrl: photoURL || null,
      assignedTasks: [],
      favoriteStations: [],
      open: true,
      timerOpen: false,
      online: true,
      plan: "Free",
      notfSettings: DEFAULT_NOTIFICATION_SETTINGS,
      createdAt: fieldValue.serverTimestamp(),
      bio: "",
      age: null,
      preferences: {
        interactionSounds: true,
        pushAndTalkMic: false,
      },
      profileAgeVisibility: "hidden",
      emailVerified: !!firebaseUser.emailVerified,
    };

    await userRef.set(publicProfile);

    if (dateOfBirth) {
      await savePrivateDateOfBirth(uid, dateOfBirth);
    }

    return publicProfile;
  }

  // Repair missing defaults without overwriting existing profile choices. This
  // also recovers an Auth account whose original public-profile write failed.
  const existingProfile = snapshot.data();
  const defaultProfile = {
    uid,
    email: email || existingProfile.email || null,
    userName:
      (userName || "").trim() ||
      firebaseUser.displayName ||
      makeDefaultDisplayName(email),
    imgAvatar: imgAvatar || null,
    imgUrl: photoURL || null,
    imageUrl: photoURL || null,
    assignedTasks: [],
    favoriteStations: [],
    open: true,
    timerOpen: false,
    online: true,
    plan: "Free",
    notfSettings: DEFAULT_NOTIFICATION_SETTINGS,
    createdAt: fieldValue.serverTimestamp(),
    bio: "",
    age: null,
    preferences: {
      interactionSounds: true,
      pushAndTalkMic: false,
    },
    profileAgeVisibility: "hidden",
  };
  const identityPatch = {};

  if (existingProfile.uid !== uid) identityPatch.uid = uid;
  if (existingProfile.email !== defaultProfile.email) {
    identityPatch.email = defaultProfile.email;
  }

  Object.entries(defaultProfile).forEach(([key, value]) => {
    if (existingProfile[key] === undefined) identityPatch[key] = value;
  });

  if (Object.keys(identityPatch).length > 0) {
    await userRef.set(identityPatch, { merge: true });
  }

  if (dateOfBirth) {
    await savePrivateDateOfBirth(uid, dateOfBirth);
  }

  return { ...existingProfile, ...identityPatch };
};

export const savePrivateDateOfBirth = async (uid, dateOfBirth) => {
  if (!uid || auth.currentUser?.uid !== uid) {
    throw new Error("auth/user-mismatch");
  }

  if (!isAtLeast16(dateOfBirth)) {
    throw new Error("profile/age-requirement");
  }

  const ref = db.collection(PRIVATE_USER_COLLECTION).doc(uid);
  const snapshot = await ref.get();
  const data = {
    uid,
    dateOfBirth: toDateOnlyString(dateOfBirth),
    updatedAt: fieldValue.serverTimestamp(),
  };

  if (!snapshot.exists || !snapshot.data()?.createdAt) {
    data.createdAt = fieldValue.serverTimestamp();
  }

  await ref.set(data, { merge: true });
  markPrivateBirthdayComplete(uid);
  return data.dateOfBirth;
};

// Moves DOB fields written by the earlier mobile flow out of the public user
// document. This keeps existing pre-release accounts from being prompted again.
export const ensurePrivateBirthday = async (
  uid,
  publicUserData = {},
  suppliedPrivateUserData,
) => {
  const privateRef = db.collection(PRIVATE_USER_COLLECTION).doc(uid);
  let privateUserData = suppliedPrivateUserData;

  if (suppliedPrivateUserData === undefined) {
    const privateSnapshot = await privateRef.get();
    privateUserData = privateSnapshot.exists ? privateSnapshot.data() : null;
  }

  const existingDate = privateUserData?.dateOfBirth;

  if (fromDateOnlyString(existingDate) && isAtLeast16(fromDateOnlyString(existingDate))) {
    markPrivateBirthdayComplete(uid);
    return true;
  }

  const legacyDate = legacyValueToDateOnly(
    publicUserData.dateOfBirthTs || publicUserData.dateOfBirth,
  );
  const parsedLegacyDate = fromDateOnlyString(legacyDate);

  if (!parsedLegacyDate || !isAtLeast16(parsedLegacyDate)) return false;

  const batch = db.batch();
  batch.set(
    privateRef,
    {
      uid,
      dateOfBirth: legacyDate,
      createdAt: privateUserData?.createdAt || fieldValue.serverTimestamp(),
      updatedAt: fieldValue.serverTimestamp(),
    },
    { merge: true },
  );

  batch.set(
    db.collection("users").doc(uid),
    {
      dateOfBirth: fieldValue.delete(),
      dateOfBirthTs: fieldValue.delete(),
    },
    { merge: true },
  );

  await batch.commit();
  markPrivateBirthdayComplete(uid);
  return true;
};

export const syncVerifiedUser = async (
  firebaseUser,
  publicUserData = {},
  { forceTokenRefresh = false } = {},
) => {
  if (!firebaseUser?.uid || !firebaseUser.emailVerified) return publicUserData;

  const userRef = db.collection("users").doc(firebaseUser.uid);
  // Required immediately after Firebase Auth changes emailVerified so future
  // rules can safely use request.auth.token.email_verified.
  if (forceTokenRefresh) await firebaseUser.getIdToken(true);

  if (publicUserData.emailVerified !== true) {
    await userRef.set(
      {
        emailVerified: true,
        verifiedAt: fieldValue.serverTimestamp(),
      },
      { merge: true },
    );
  }

  return { ...publicUserData, emailVerified: true };
};
